import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Briefcase, Calendar, MapPin, User, Eye, Trash2 } from "lucide-react";

import JobDescriptionModal from "../Modals/JobDescriptionModal";
import AssignRecruiterModal from "../Modals/AssignRecruiterModal";
import AssignConsultantModal from "../Modals/AssignConsultantModal";

import {
	getJobDetails,
	getPublishJobsByUID,
	unassignJobToEmployee,
	unassignJobToConsultant,
} from "../Services/DraftJobService";

import Cookies from "js-cookie";
import ConfirmationAlert from "../../../../Alerts/ConfirmationAlert";
import NormalAlert from "../../../../Alerts/NormalAlert";

const ViewJobDetails = () => {
	const { jobUID } = useParams();
	const navigate = useNavigate();

	const [jobDetails, setJobDetails] = useState(null);
	const [loading, setLoading] = useState(true);

	const [showRecruiterModal, setShowRecruiterModal] = useState(false);
	const [showConsultantModal, setShowConsultantModal] = useState(false);
	const [showJDModal, setShowJDModal] = useState(false);

	const [confirmAlert, setConfirmAlert] = useState(null);
	const [alert, setAlert] = useState(null);

	const token = Cookies.get("employeeToken");

	useEffect(() => {
		fetchJobDetails();
	}, [jobUID]);

	const fetchJobDetails = async () => {
		try {
			const res = await getJobDetails(jobUID);

			setJobDetails({
				...res.data.jobData[0],
				assignedEmployee: res.data.assignedEmployee || [],
				assignedConsultant: res.data.assignedConsultant || [],
			});
		} catch (error) {
			setAlert({
				type: "fail",
				message:
					error?.response?.data?.message || "Unable to fetch job details.",
			});
		} finally {
			setLoading(false);
		}
	};

	const formatIndianCurrency = (amount) =>
		new Intl.NumberFormat("en-IN").format(amount);

  const formatDate = (dateStr) => {
      if (!dateStr) return "";

      try {
        return dateStr.split("T")[0];
      } catch {
        return "";
      }
    };

	const handlePublishJob = async () => {
		try {
			const res = await getPublishJobsByUID(jobUID);

			setAlert({
				type: res.data.status,
				message: res.data.message,
			});

			if (res.data.status === "Success") {
				navigate("/Dashboard/Live-Jobs");
			}
		} catch (error) {
			setAlert({
				type: "fail",
				message: error?.response?.data?.message,
			});
		}
	};

	const handleDeleteRecruiter = (assignedUID, employeeCode) => {
		setConfirmAlert({
			message: "Remove this recruiter?",
			onConfirm: async () => {
				const res = await unassignJobToEmployee(jobUID, employeeCode, token);

				setAlert({
					type: res.data.status,
					message: res.data.message,
				});

				fetchJobDetails();
			},
		});
	};

	const handleDeleteConsultant = (assignedUID, consultantCode) => {
		setConfirmAlert({
			message: "Remove this consultant?",
			onConfirm: async () => {
				const res = await unassignJobToConsultant(
					jobUID,
					consultantCode,
					token,
				);

				setAlert({
					type: res.data.status,
					message: res.data.message,
				});

				fetchJobDetails();
			},
		});
	};

	if (loading) return <div className="p-10">Loading...</div>;
	if (!jobDetails) return <div>No Job Found</div>;

	const InfoCard = ({ label, value }) => (
		<div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition">
			<p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
				{label}
			</p>
			<p className="font-semibold text-gray-800 text-sm">{value || "-"}</p>
		</div>
	);

	const SectionHeader = ({ icon: Icon, title }) => (
		<div className="flex items-center gap-3 mb-4 border-b pb-2">
			<div className="p-2 bg-blue-50 rounded-lg">
				<Icon size={18} className="text-blue-600" />
			</div>
			<h3 className="text-lg font-semibold text-gray-800">{title}</h3>
		</div>
	);

	return (
		<div className="min-h-screen bg-gray-100 px-8 py-10 mt-[55px]">
			<div className="max-w-6xl mx-auto space-y-8">
				{/* HEADER */}
				<div className="bg-master-header rounded-xl shadow-sm p-5 border">
					<div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
						<div>
							<h1 className="text-3xl font-bold text-white">
								{jobDetails.jobTitle} ({jobDetails.roleCode})
							</h1>

							<div className="flex gap-3 mt-3 flex-wrap">
								<span className="px-3 py-1 text-sm rounded-full bg-green-100 text-green-700">
									{jobDetails.positionType}
								</span>

								<span className="px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-700">
									{jobDetails.numberOfOpenings} Openings
								</span>
							</div>
						</div>

						<div className="flex gap-4">
							<button
								onClick={() => setShowJDModal(true)}
								className="flex items-center gap-2 px-4 py-2 bg-[linear-gradient(60deg,#0D47A1,#3381cf)] rounded-lg text-white"
							>
								<Eye size={16} />
								View Job Description
							</button>

							{jobDetails.isPublish === "True" ? (
								<Link
									to={`/Dashboard/Submit-Candidates/${jobUID}`}
									className="px-5 py-2 bg-[linear-gradient(60deg,#0D47A1,#3381cf)] text-white rounded-lg"
								>
									Submit Candidate
								</Link>
							) : (
								<button
									onClick={() =>
										setConfirmAlert({
											message: "Publish this job?",
											onConfirm: handlePublishJob,
										})
									}
									className="px-5 py-2 bg-[linear-gradient(60deg,#0D47A1,#3381cf)] text-white rounded-lg"
								>
									Publish Job
								</button>
							)}
						</div>
					</div>
				</div>

				{/* ROLE + MANAGER */}
				<div className="grid lg:grid-cols-2 gap-8">
					<div className="bg-white rounded-xl shadow-sm p-8 border">
						{/* Header */}
						<div className="flex items-center justify-between mb-3">
							<div className="flex items-center gap-2">
								<Briefcase className="text-blue-500" size={20} />
								<h2 className="text-lg font-semibold text-gray-800">
									Role Information
								</h2>
							</div>

							{/* Badge */}
							<span
								className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold ${
									jobDetails.roleCategory === "on-roll"
										? "bg-green-100 text-green-700 border border-green-300"
										: "bg-orange-100 text-orange-700 border border-orange-300"
								}`}
							>
								{jobDetails.roleCategory}
							</span>
						</div>

						<div className="border-b border-gray-200 mb-6"></div>
						{/* Content */}
						<div className="grid md:grid-cols-2 gap-6">
							<InfoCard label="Function" value={jobDetails.function} />
							<InfoCard label="Department" value={jobDetails.department} />
							<InfoCard label="Grade" value={jobDetails.grade} />
							<InfoCard label="Job Category" value={jobDetails.jobCategory} />
						</div>
					</div>

					<div className="bg-white rounded-xl shadow-sm p-8 border">
						<SectionHeader icon={User} title="Managers" />
						<div className="grid md:grid-cols-2 gap-6">
							<InfoCard
								label="Reporting Manager"
								value={`${jobDetails.reportingManageraName} (${jobDetails.reportingManagerID})`}
							/>
							<InfoCard
								label="Functional Manager"
								value={`${jobDetails.functionalManageraName} (${jobDetails.functionalManagerID})`}
							/>
						</div>
					</div>
				</div>

				{/* LOCATION + COMPENSATION */}
				<div className="grid lg:grid-cols-2 gap-8">
					<div className="bg-white rounded-xl shadow-sm p-8 border">
						<SectionHeader icon={MapPin} title="Location" />
						<div className="grid md:grid-cols-2 gap-6">
							<InfoCard label="Location" value={jobDetails.location} />
							<InfoCard label="Zone" value={jobDetails.zone} />
						</div>
					</div>

					<div className="bg-white rounded-xl shadow-sm p-8 border">
						<SectionHeader icon={Calendar} title="Compensation & Experience" />
						<div className="grid md:grid-cols-2 gap-6">
							<InfoCard
								label="Budget"
								value={formatIndianCurrency(jobDetails.offeredSalary)}
							/>
							<InfoCard
								label="Experience"
								value={`${jobDetails.minimumExperience} - ${jobDetails.maximumExperience}`}
							/>
							<InfoCard
								label="Qualification"
								value={jobDetails.minimumQualification}
							/>
							<InfoCard
								label="Gender Preference"
								value={jobDetails.genderPreference}
							/>
							<InfoCard
								label="Requisition Date"
								value={formatDate(jobDetails.requisitionDate)}
							/>
						</div>
					</div>
				</div>

				{/* RECRUITERS + CONSULTANTS */}
				<div className="space-y-8">
					{/* Recruiters */}
					<div className="bg-white rounded-xl p-6 border shadow-sm">
						<h3 className="font-semibold text-lg mb-4">Assigned Recruiters</h3>

						<div className="flex flex-wrap gap-3">
							{jobDetails.assignedEmployee.map((emp) => (
								<div
									key={emp.assignedUID}
									className="flex items-center justify-between w-[320px] bg-gray-50 rounded-lg px-3 py-2 border"
								>
									<div className="pr-3">
										<p className="text-sm font-medium text-gray-800 leading-tight">
											{emp.employeeName} ({emp.employeeCode})
										</p>
										<p className="text-xs text-gray-500">{emp.email}</p>
									</div>

									<button
										onClick={() =>
											handleDeleteRecruiter(emp.assignedUID, emp.employeeCode)
										}
										className="text-red-500 hover:text-red-600"
									>
										<Trash2 size={16} />
									</button>
								</div>
							))}
						</div>
						<button
							onClick={() => setShowRecruiterModal(true)}
							className="mt-3 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white text-sm"
						>
							Assign Recruiter
						</button>
					</div>

					{/* Consultants */}
					<div className="bg-white rounded-xl p-6 border shadow-sm">
						<h3 className="font-semibold text-lg mb-4">Assigned Consultants</h3>

						<div className="flex flex-wrap gap-3">
							{jobDetails.assignedConsultant.map((con) => (
								<div
									key={con.assignedUID}
									className="flex items-center justify-between w-[320px] bg-gray-50 rounded-lg px-3 py-2 border"
								>
									<div className="pr-3">
										<p className="text-sm font-medium text-gray-800 leading-tight">
											{con.consultantName} ({con.consultantCode})
										</p>
										<p className="text-xs text-gray-500">{con.email}</p>
									</div>

									<button
										onClick={() =>
											handleDeleteConsultant(
												con.assignedUID,
												con.consultantCode,
											)
										}
										className="text-red-500 hover:text-red-600"
									>
										<Trash2 size={16} />
									</button>
								</div>
							))}
						</div>

						<button
							onClick={() => setShowConsultantModal(true)}
							className="mt-3 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white text-sm"
						>
							Assign Consultant
						</button>
					</div>
				</div>
			</div>

			<AssignRecruiterModal
				show={showRecruiterModal}
				onClose={() => setShowRecruiterModal(false)}
				jobUID={jobUID}
				onSuccess={() => fetchJobDetails()}
			/>

			<AssignConsultantModal
				show={showConsultantModal}
				onClose={() => setShowConsultantModal(false)}
				jobUID={jobUID}
				onSuccess={() => fetchJobDetails()}
			/>

			<JobDescriptionModal
				show={showJDModal}
				onHide={() => setShowJDModal(false)}
				fullPath={jobDetails.jobDescription}
			/>

			{confirmAlert && (
				<ConfirmationAlert
					message={confirmAlert.message}
					onConfirm={() => {
						setConfirmAlert(null);
						confirmAlert.onConfirm();
					}}
					onCancel={() => setConfirmAlert(null)}
				/>
			)}

			{alert && (
				<NormalAlert
					type={alert.type}
					message={alert.message}
					onClose={() => setAlert(null)}
				/>
			)}
		</div>
	);
};

export default ViewJobDetails;
